import {Button, Center, Modal, VStack} from 'native-base';
import React, {useCallback, useEffect, useRef, useState} from 'react';
import {ScrollView, View, StyleSheet} from 'react-native';
import Webcam from 'react-webcam';

// @ts-nocheck
export default function WebcamCapture({onCapture, onCameraError}) {
  const [modalVisible, setModalVisible] = React.useState(false);
  const webcamRef = useRef(null);
  const [imgSrc, setImgSrc] = useState(null);
  const [videoDevices, setVideoDevices] = useState([]);
  const [selectedDeviceId, setSelectedDeviceId] = useState('');
  const [cameraError, setCameraError] = useState('');

  const handleSizeClick = () => {
    setModalVisible(!modalVisible);
  };

  const loadVideoDevices = useCallback(async () => {
    if (
      typeof navigator === 'undefined' ||
      !navigator.mediaDevices ||
      !navigator.mediaDevices.enumerateDevices
    ) {
      return;
    }

    try {
      const devices = await navigator.mediaDevices.enumerateDevices();
      const cameras = devices.filter(d => d.kind === 'videoinput');
      setVideoDevices(cameras);
      if (cameras.length === 0) {
        const message =
          'No camera detected. Please choose file upload or skip for now.';
        setCameraError(message);
        if (onCameraError) {
          onCameraError(message);
        }
      } else {
        setCameraError('');
      }
      if (cameras.length > 0 && !selectedDeviceId) {
        setSelectedDeviceId(cameras[0].deviceId);
      }
    } catch (error) {
      const message = 'Unable to list available cameras.';
      setCameraError(message);
      if (onCameraError) {
        onCameraError(message);
      }
    }
  }, [onCameraError, selectedDeviceId]);

  useEffect(() => {
    if (modalVisible) {
      loadVideoDevices();
    }
  }, [modalVisible, loadVideoDevices]);

  const handleUserMediaError = useCallback(
    error => {
      const message =
        error?.message ||
        'Camera is unavailable. Please choose file upload or skip for now.';
      setCameraError(message);
      if (onCameraError) {
        onCameraError(message);
      }
    },
    [onCameraError],
  );

  const capture = useCallback(() => {
    const imageSrc = webcamRef.current.getScreenshot();
    if (imageSrc) {
      setCameraError('');
      setImgSrc(imageSrc);
      return;
    }
    const message = 'Could not capture photo from the selected camera.';
    setCameraError(message);
    if (onCameraError) {
      onCameraError(message);
    }
  }, [onCameraError]);

  const videoConstraints = selectedDeviceId
    ? {deviceId: {exact: selectedDeviceId}}
    : {facingMode: 'user'};

  const cameraPickerLabelStyle = {
    fontSize: 14,
    color: '#333',
    marginRight: 8,
  };

  const cameraPickerStyle = {
    flex: 1,
    height: 36,
    borderRadius: 6,
    border: '1px solid #d4d4d8',
    padding: '0 8px',
    backgroundColor: '#fff',
  };

  const cameraErrorTextStyle = {
    color: '#b91c1c',
    fontSize: 13,
    lineHeight: '18px',
  };

  return (
    <>
      <Modal isOpen={modalVisible} onClose={() => setModalVisible(false)} size={'xl'}>
        <Modal.Content maxH="600">
          <Modal.CloseButton />
          <Modal.Header>Capture Photo</Modal.Header>
          <Modal.Body>
            <ScrollView
              contentContainerStyle={styles.scrollViewContainer}
            >
              {!imgSrc && (
                <View style={styles.cameraControlsContainer}>
                  {videoDevices.length > 1 && (
                    <View style={styles.cameraPickerContainer}>
                      <label
                        htmlFor="webcam-camera-select"
                        style={cameraPickerLabelStyle}
                      >
                        Camera
                      </label>
                      <select
                        id="webcam-camera-select"
                        value={selectedDeviceId}
                        onChange={e => {
                          setImgSrc(null);
                          setCameraError('');
                          setSelectedDeviceId(e.target.value);
                        }}
                        style={cameraPickerStyle}
                      >
                        {videoDevices.map((device, index) => (
                          <option key={device.deviceId || index} value={device.deviceId}>
                            {device.label || `Camera ${index + 1}`}
                          </option>
                        ))}
                      </select>
                    </View>
                  )}
                  <Button variant="outline" onPress={loadVideoDevices}>
                    Refresh cameras
                  </Button>
                </View>
              )}
              {!!cameraError && (
                <View style={styles.cameraErrorContainer}>
                  <span style={cameraErrorTextStyle}>{cameraError}</span>
                </View>
              )}
              {imgSrc ? (
                <img src={imgSrc} alt="webcam" />
              ) : (
                <Webcam
                  screenshotFormat="image/jpeg"
                  audio={false}
                  height={600}
                  width={600}
                  ref={webcamRef}
                  videoConstraints={videoConstraints}
                  onUserMedia={() => setCameraError('')}
                  onUserMediaError={handleUserMediaError}
                />
              )}
            </ScrollView>
          </Modal.Body>
          <Modal.Footer>
            <View style={styles.footerContainer}>
              {!!imgSrc ? (
                <Button
                  variant="outline"
                  colorScheme="blueGray"
                  onPress={() => {
                    setImgSrc(null);
                  }}>
                  Retake
                </Button>
              ) : (
                <Button onPress={capture}>Take</Button>
              )}
              {!!imgSrc && (
                <Button
                  onPress={() => {
                    /**
                     * Close modal and forward the image to parent....
                     */
                    onCapture(imgSrc.split(',')[1])
                    setModalVisible(false);
                    setImgSrc(null);
                  }}>
                  Save
                </Button>
              )}
            </View>
          </Modal.Footer>
        </Modal.Content>
      </Modal>
      <Center>
        <VStack space={4}>
          <Button
            style={styles.takePhotoButton}
            variant="ghost"
            size="lg"
            py="4"
            onPress={() => handleSizeClick()}>
            Take photo
          </Button>
        </VStack>
      </Center>
    </>
  );
}

const styles = StyleSheet.create({
  scrollViewContainer: {
    flex: 1,
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
  },
  cameraControlsContainer: {
    width: '100%',
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 12,
  },
  cameraPickerContainer: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
    marginRight: 8,
  },
  cameraErrorContainer: {
    width: '100%',
    marginBottom: 12,
    borderRadius: 6,
    backgroundColor: '#fef2f2',
    borderWidth: 1,
    borderColor: '#fecaca',
    paddingHorizontal: 10,
    paddingVertical: 8,
  },
  footerContainer: {
    flex: 1,
    flexDirection: 'row',
    justifyContent: 'space-between',
  },
  takePhotoButton: {
    flex: 1,
    marginTop: 4,
    marginBottom: 16,
  },
});
